
import {
  computed,
  defineComponent, watch, ref, PropType, SetupContext, onBeforeUnmount, onMounted,
} from 'vue';
import SegmentationBlockAND from '@/components/segmentations/SegmentationBlockAND.vue';
import { useI18n } from 'vue-i18n';
import { store } from '@/store';
import Loader from '@/components/layout/Loader.vue';
import axios from 'axios';
import { UserState } from '@/composables/User';
import { showToastError, showToastSuccess } from '@/helpers';
import SegmentationPromptAI from '@/components/segmentations/SegmentationPromptAI.vue';
import { TRIGGER_SEGMENTATION_ORIGIN } from '@/composables/segmentations/triggerSegmentation';

export default defineComponent({
  name: 'SegmentationsPanel',
  components: {
    SegmentationPromptAI,
    Loader,
    SegmentationBlockAND,
  },

  props: {
    modelValue: {
      type: Object as PropType<any>,
      required: false,
      default: () => ({}),
    },

    origin: {
      type: String,
      required: false,
      default: '',
    },

    size: {
      type: String,
      required: false,
      default: '',
    },

    showPromptAI: {
      type: Boolean,
      required: false,
      default: false,
    },

    theme: {
      type: [String, Array] as PropType<string | string[]>,
      required: false,
      default: '',
    },

    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },

    triggerCount: {
      type: Number,
      required: false,
      default: 0,
    },

    // Flow context for new flow system (events metadata support)
    flowData: {
      type: Object as PropType<Record<string, any> | null>,
      required: false,
      default: null,
    },

    operatorStepId: {
      type: String,
      required: false,
      default: '',
    },

    // Collection contexts for parent loops (custom collections, products, etc.)
    collectionContexts: {
      type: Array as PropType<{ collection: string; iterator: string; schema?: any }[]>,
      required: false,
      default: () => [],
    },
  },

  emits: ['update:modelValue', 'count-updated'],

  setup(props: {
    modelValue: any;
    origin: string;
    size: string;
    showPromptAI: boolean;
    theme: string | string[];
    readonly: boolean;
    triggerCount: number;
    flowData: any;
    operatorStepId: string;
    collectionContexts: { collection: string; iterator: string; schema?: any }[];
  }, { emit }: SetupContext) {
    const { t } = useI18n();
    const idShop = UserState?.activeShop?.id ?? 0;
    const cleanedDataKey = ref(0);
    const AIGenerationInProgress = ref(false);
    const segmentsConfiguration: any = computed(() => store.getters['segmentationsEditor/getConfiguration']);

    // In readonly mode, use modelValue directly; otherwise use store
    const readonlyConfig = computed(() => {
      if (props.readonly) {
        return props.modelValue || [];
      }
      return segmentsConfiguration.value;
    });

    const refreshComponents: any = computed(() => store.getters['segmentationsEditor/getRefreshComponents']);

    const getInitialized: any = computed(() => store.getters['segmentationsEditor/getInitialized']);

    const refreshContactsNumber = computed(() => store.getters['segmentationsEditor/getRefreshContactsNumber']);

    const loadContactsNumber = ref(false);

    const totalContactsNumber: any = ref(0);

    const cleanData = (data: any) => {
      const cleanedData = data.map((subArray: any) => subArray.filter((obj: any) => Object.keys(obj).length !== 0));
      return cleanedData.filter((subArray: any) => subArray.length > 0);
    };

    const handleContactsNumber = async () => {
      // A trigger's segmentation is evaluated per visitor when the page loads:
      // a contact count means nothing there, and its visit criteria are unknown
      // to the counting query.
      if (props.origin === TRIGGER_SEGMENTATION_ORIGIN) {
        store.commit('segmentationsEditor/setRefreshContactsNumber', false);
        return;
      }
      emit('count-updated', { count: null, loading: true });
      loadContactsNumber.value = true;
      const tmpCleanedData = cleanData(segmentsConfiguration.value);
      try {
        const result = await axios.post(
          `${process.env.VUE_APP_NESTJS_SEGMENTATION_API_URL}/count`,
          {
            idShop,
            config: tmpCleanedData,
          },
        );

        if (result && (result.status === 200 || result.status === 201)) {
          if (result.data.error) {
            totalContactsNumber.value = null;
          } else {
            totalContactsNumber.value = result.data;
          }
        } else {
          totalContactsNumber.value = 0;
          await showToastError('GENERIC_ERROR');
        }
      } catch (e) {
        totalContactsNumber.value = 0;
        await showToastError('GENERIC_ERROR');
      }

      loadContactsNumber.value = false;
      store.commit('segmentationsEditor/setRefreshContactsNumber', false);

      // Emit count result to parent
      emit('count-updated', {
        count: totalContactsNumber.value,
        loading: false,
      });
    };

    const handleGenerationByAI = async (generatedConfig: any) => {
      if (generatedConfig.success === false) {
        AIGenerationInProgress.value = false;
        await showToastError(t('segmentations.promptAI.error'));
        return;
      }

      store.commit('segmentationsEditor/setConfiguration', generatedConfig.config);
      await handleContactsNumber();
      AIGenerationInProgress.value = false;
      await showToastSuccess(t('segmentations.promptAI.success'));
    };

    const moveBlockAndToBlockOr = (index: any) => {
      store.commit('segmentationsEditor/moveBlockAndToBlockOr', index);
      handleContactsNumber();
    };

    watch([segmentsConfiguration], () => {
      if (!segmentsConfiguration.value.length || !segmentsConfiguration.value) {
        store.commit('segmentationsEditor/initConfiguration');
      }
      emit('update:modelValue', segmentsConfiguration.value);
    }, { deep: true });

    watch([refreshContactsNumber], async () => {
      // TODO : only if comming from import page
      if (refreshContactsNumber.value) {
        await handleContactsNumber();
      }
    });

    // Watch for triggerCount changes from parent to refresh count
    // Wait for store to be initialized before processing
    watch(() => props.triggerCount, async (newVal, oldVal) => {
      if (newVal !== oldVal && newVal > 0 && getInitialized.value) {
        await handleContactsNumber();
      }
    });

    // Watch for theme changes and reinitialize
    watch(() => props.theme, async (newTheme, oldTheme) => {
      if (newTheme !== oldTheme && getInitialized.value) {
        await store.dispatch('segmentationsEditor/initialization',
          {
            originCall: props.origin,
            configSegmentation: props.modelValue,
            size: props.size,
            theme: newTheme,
            flow: props.flowData,
            operatorStepId: props.operatorStepId,
            collectionContexts: props.collectionContexts,
          });
      }
    });

    // Watch for collectionContexts changes and reinitialize (async detection)
    watch(() => props.collectionContexts, async (newContexts, oldContexts) => {
      // Reinitialize when contexts are detected (handles async detection race condition)
      if (newContexts && newContexts.length > 0) {
        // Only reinitialize if contexts actually changed
        const hasChanged = !oldContexts
          || oldContexts.length === 0
          || newContexts.length !== oldContexts.length
          || newContexts.some((ctx, i) => ctx.collection !== oldContexts[i]?.collection);

        if (hasChanged) {
          await store.dispatch('segmentationsEditor/initialization',
            {
              originCall: props.origin,
              configSegmentation: props.modelValue,
              size: props.size,
              theme: props.theme,
              flow: props.flowData,
              operatorStepId: props.operatorStepId,
              collectionContexts: newContexts,
            });
        }
      }
    }, { deep: true });

    onMounted(async () => {
      await store.dispatch('segmentationsEditor/initialization',
        {
          originCall: props.origin || 'conditional',
          configSegmentation: props.modelValue,
          size: props.size,
          theme: props.theme,
          flow: props.flowData,
          operatorStepId: props.operatorStepId,
          collectionContexts: props.collectionContexts,
        });
    });

    onBeforeUnmount(() => {
      // Don't reset state in readonly mode to avoid side effects
      if (!props.readonly) {
        store.commit('segmentationsEditor/resetState');
      }
    });

    return {
      t,
      cleanedDataKey,
      getInitialized,
      refreshComponents,
      segmentsConfiguration,
      readonlyConfig,
      handleGenerationByAI,
      AIGenerationInProgress,
      moveBlockAndToBlockOr,
    };
  },
});
